/**
 * Обработчики WebSocket событий
 */

import store from '../redux/store';
import {
  setIsGitLoading,
  setGitProgress,
  setGitTaskId,
  incrementGitContextVersion,
} from '../redux/reducers';
import { GitIndexIvent, ChatMessageEvent, ProcessEvent } from './types';
import { globalSnackbar } from '../components/GlobalSnackbar';
import { contextDocumentEvents } from '../modules/chat/utils/contextDocumentEvents';
import type { ContextDocumentParsingWsEvent } from '../modules/estimation/types';

const GIT_INDEXING_STATE_KEY = 'git_indexing_state';

// Колбэки для обработки событий чата (регистрируются из компонента Chat)
let chatMessageCallback: ((data: ChatMessageEvent) => void) | null = null;

export const registerChatMessageCallback = (callback: (data: ChatMessageEvent) => void) => {
  chatMessageCallback = callback;
};

export const unregisterChatMessageCallback = () => {
  chatMessageCallback = null;
};

// Колбэки для обработки событий процессов саммаризатора
let processEventCallback: ((event: string, data: ProcessEvent) => void) | null = null;

export const registerProcessEventCallback = (
  callback: (event: string, data: ProcessEvent) => void
) => {
  processEventCallback = callback;
};

export const unregisterProcessEventCallback = () => {
  processEventCallback = null;
};

/**
 * Основной обработчик WebSocket событий
 */
export const handleWebSocketEvent = (
  eventName: string,
  data: GitIndexIvent | ChatMessageEvent | unknown
) => {
  // Обработка различных типов событий
  switch (eventName) {
    case 'pusher:subscription_succeeded':
    case 'pusher:subscription_error':
      // Системные события Pusher, обрабатываются в хуке
      break;

    case 'git-index':
      handleGitIndex(data as GitIndexIvent);
      break;

    case 'chat-message':
      handleChatMessage(data as ChatMessageEvent);
      break;

    case 'process-transcription':
    case 'process-summarization':
    case 'process-agreement-extraction':
    case 'process-context-document-parsing':
      if (eventName === 'process-context-document-parsing') {
        contextDocumentEvents.emit(data as ContextDocumentParsingWsEvent);
      }
      if (processEventCallback) {
        processEventCallback(eventName, data as ProcessEvent);
      }
      break;

    default:
      console.log('Unhandled event:', eventName, data);
  }
};

/**
 * Обработчик события git-index
 */
const handleGitIndex = async (data: GitIndexIvent) => {
  // Проверяем, что task_id совпадает с текущим
  const state = store.getState();
  const currentTaskId = state.reducer.gitTaskId;
  const currentProjectId = (state.reducer.project as { id?: string })?.id || null;

  if (!currentTaskId || data.task_id !== currentTaskId) {
    console.log('Git index event ignored: task_id mismatch', {
      received: data.task_id,
      expected: currentTaskId,
    });
    return;
  }

  // Используем dispatch из store напрямую
  if (data.status === 'stream' && data.progress) {
    // Обработка прогресса индексации
    store.dispatch(
      setGitProgress({
        value: data.progress.value,
        label: `${data.progress.label}\u00A0\u00A0`,
      })
    );

    // Сохраняем состояние индексации в localStorage
    try {
      const stateToSave = {
        projectId: currentProjectId,
        taskId: data.task_id,
        isLoading: true,
        progress: {
          value: data.progress.value,
          label: `${data.progress.label}\u00A0\u00A0`,
        },
        timestamp: Date.now(),
      };
      console.log('[Git] Сохранение состояния индексации:', stateToSave);
      localStorage.setItem(GIT_INDEXING_STATE_KEY, JSON.stringify(stateToSave));
      console.log('[Git] Состояние индексации успешно сохранено');
    } catch (error) {
      console.error('[Git] Ошибка сохранения состояния индексации:', error);
    }
  } else if (data.status === 'success') {
    globalSnackbar({
      message: `Индексация репозитория успешно завершена`,
      severity: 'success',
      autoClose: false, // Не закрывается автоматически
    });
    store.dispatch(setIsGitLoading(false));
    store.dispatch(incrementGitContextVersion());
    // Сбрасываем прогресс и task_id
    store.dispatch(
      setGitProgress({
        value: 0,
        label: '',
      })
    );
    store.dispatch(setGitTaskId(null));

    // Очищаем сохраненное состояние индексации
    try {
      localStorage.removeItem(GIT_INDEXING_STATE_KEY);
      console.log('[Git] Состояние индексации очищено (success)');
    } catch (error) {
      console.error('[Git] Ошибка очистки состояния индексации:', error);
    }
  } else if (data.status === 'error') {
    globalSnackbar({
      message: `Во время индексации репозитория возникли проблемы`,
      severity: 'error',
      autoClose: false, // Не закрывается автоматически
    });
    store.dispatch(setIsGitLoading(false));
    // Сбрасываем прогресс и task_id
    store.dispatch(
      setGitProgress({
        value: 0,
        label: '',
      })
    );
    store.dispatch(setGitTaskId(null));

    // Очищаем сохраненное состояние индексации
    try {
      localStorage.removeItem(GIT_INDEXING_STATE_KEY);
      console.log('[Git] Состояние индексации очищено (error)');
    } catch (error) {
      console.error('[Git] Ошибка очистки состояния индексации:', error);
    }
  }

  // Дополнительная логика обработки git-index события
  // ...
};

/**
 * Обработчик события chat-message
 */
const handleChatMessage = (data: ChatMessageEvent) => {
  if (chatMessageCallback) {
    chatMessageCallback(data);
  }
};

/**
 * Функция для восстановления состояния индексации из localStorage
 */
export const restoreGitIndexingState = (currentProjectId: string | number) => {
  try {
    console.log('[Git] Попытка восстановить состояние индексации для проекта:', currentProjectId);
    const savedStateStr = localStorage.getItem(GIT_INDEXING_STATE_KEY);

    if (!savedStateStr) {
      console.log('[Git] Сохраненное состояние не найдено');
      return null;
    }

    const savedState = JSON.parse(savedStateStr);
    console.log('[Git] Найдено сохраненное состояние:', savedState);

    // Проверяем, что состояние относится к текущему проекту
    if (savedState.projectId !== currentProjectId) {
      console.log('[Git] Состояние игнорируется: несовпадение проекта', {
        saved: savedState.projectId,
        current: currentProjectId,
      });
      // Очищаем устаревшее состояние
      localStorage.removeItem(GIT_INDEXING_STATE_KEY);
      return null;
    }

    // Проверяем, что состояние не устарело (например, старше 24 часов)
    const MAX_AGE = 24 * 60 * 60 * 1000; // 24 часа
    if (Date.now() - savedState.timestamp > MAX_AGE) {
      console.log('[Git] Состояние устарело, очищаем...');
      localStorage.removeItem(GIT_INDEXING_STATE_KEY);
      return null;
    }

    console.log('[Git] Состояние успешно восстановлено');
    return savedState;
  } catch (error) {
    console.error('[Git] Ошибка восстановления состояния индексации:', error);
    return null;
  }
};

/**
 * Функция для очистки состояния индексации
 */
export const clearGitIndexingState = () => {
  try {
    localStorage.removeItem(GIT_INDEXING_STATE_KEY);
    console.log('[Git] Состояние индексации очищено');
  } catch (error) {
    console.error('[Git] Ошибка очистки состояния индексации:', error);
  }
};
